import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { FileNode } from "../api.ts";
import { formatCount, formatDateTime } from "../lib/format.ts";
import { audienceQuery, sharesQuery } from "../lib/queries.ts";
import { splitShares } from "../lib/shares.ts";
import { CopyButton } from "./CopyButton.tsx";
import { DeleteNodeButton } from "./DeleteNodeButton.tsx";
import { FolderBadge } from "./FileBadge.tsx";
import styles from "./FolderView.module.css";
import { AccessPanel } from "./file/AccessPanel.tsx";
import { audienceLabel } from "./file/AudienceSummary.tsx";
import filePage from "./file/FilePage.module.css";
import { RenameForm } from "./file/RenameForm.tsx";
import { Icon } from "./Icon.tsx";
import { MoveButton } from "./MoveDialog.tsx";
import { NewFolderButton } from "./NewFolderButton.tsx";
import { PickFiles } from "./PickFiles.tsx";
import { ShareLinksPanel } from "./shares/ShareLinksPanel.tsx";
import ui from "./ui.module.css";
import { FolderStorageMeta } from "./usage/NodeUsageFacts.tsx";

export function FolderHeader({ node }: { node: FileNode }) {
  const [renaming, setRenaming] = useState(false);
  const pencil = useRef<HTMLButtonElement>(null);

  const doneRenaming = () => {
    flushSync(() => setRenaming(false));
    pencil.current?.focus();
  };

  return (
    <header className={filePage.header}>
      <FolderBadge size="lg" />
      <div className={filePage.identity}>
        {renaming ? (
          <RenameForm node={node} onDone={doneRenaming} />
        ) : (
          <div className={filePage.titleRow}>
            <h1 className={filePage.title} style={{ viewTransitionName: `file-${node.id}` }}>
              {node.name}
            </h1>
            <button
              ref={pencil}
              type="button"
              className={`${ui.iconButton} ${filePage.rename}`}
              data-variant="ghost"
              onClick={() => setRenaming(true)}
              aria-label={`Rename ${node.name}`}
              title="Rename"
            >
              <Icon name="pencil" />
            </button>
          </div>
        )}
        <ul className={filePage.meta}>
          <li>{node.childCount === 0 ? "Empty" : formatCount(node.childCount, "item", "items")}</li>
          {node.childCount > 0 ? <FolderStorageMeta id={node.id} /> : null}
          <li>
            Created <time dateTime={node.createdAt}>{formatDateTime(node.createdAt)}</time>
          </li>
          {node.public ? (
            <li data-public>
              <Icon name="globe" className={filePage.metaIcon} />
              Public · no sign-in
            </li>
          ) : null}
        </ul>
      </div>
      <div className={filePage.headerActions}>
        <PickFiles
          target={{ kind: "new", parentId: node.id }}
          label="Upload files"
          variant="primary"
          className={filePage.primaryAction}
        >
          <Icon name="upload" />
          Upload files
        </PickFiles>
        <CopyButton value={node.url} variant="frame" />
        <NewFolderButton parentId={node.id} variant="frame" />
        <MoveButton node={node} variant="frame" />
        <DeleteNodeButton node={node} className={filePage.deleteAction} />
      </div>
    </header>
  );
}

export function SharingPanel({ node }: { node: FileNode }) {
  const { data: audience } = useSuspenseQuery(audienceQuery(node.id));

  return (
    <details className={`${ui.card} ${styles.sharing}`}>
      <summary className={styles.sharingSummary}>
        <span className={styles.sharingMark} aria-hidden="true">
          <Icon name="people" />
        </span>
        <span className={styles.sharingSummaryText}>
          <span className={styles.sharingTitle}>Share</span>
          <span className={styles.sharingAudience}>
            {audienceLabel(audience.effective)}
            {audience.mode === "inherit" && audience.restrictedBy.length > 0
              ? ` · inherited from ${audience.restrictedBy.at(-1)?.name}`
              : ""}
          </span>
        </span>
        <Icon name="chevron" className={styles.sharingChevron} />
      </summary>
      <AccessPanel node={node} embedded />
    </details>
  );
}

export function ShareLinksDisclosure({ node }: { node: FileNode }) {
  const shares = useQuery(sharesQuery(node.id));
  const active = shares.data ? splitShares(shares.data.items).active.length : null;

  return (
    <details className={`${ui.card} ${styles.sharing}`}>
      <summary className={styles.sharingSummary}>
        <span className={styles.sharingMark} aria-hidden="true">
          <Icon name="link" />
        </span>
        <span className={styles.sharingSummaryText}>
          <span className={styles.sharingTitle}>Share links</span>
          <span className={styles.sharingAudience}>
            {active === null
              ? ""
              : active === 0
                ? "None active"
                : formatCount(active, "active link", "active links")}
          </span>
        </span>
        <Icon name="chevron" className={styles.sharingChevron} />
      </summary>
      <ShareLinksPanel node={node} embedded />
    </details>
  );
}
